import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, RotateCcw, XCircle, Copy, X, Mail, MessageCircle, Users, Clock } from 'lucide-react';
import { getCommunicationsOutbox, retryOutboundMessage, cancelOutboundMessage, getOutboundMessage } from '../../../data/api';
import { shortDateTime, titleizeStatus } from './ciUtils';
import { Loading, ComingWave, useToast } from './CiShared';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { MiloNotebookItems } from '../_shared/MiloNotebookItems';
import { isIntelligenceNotebookEnabled } from '../intelligence-notebook/intelligenceNotebookFlag';

// Outbox — every sent / scheduled / queued communication, ported to the new
// look. Wired to the real GET /api/communications/outbox. Retry + Cancel call
// the real backend endpoints. "Use again" re-opens the Create Communication
// screen pre-filled with the row's channel + template + subject (handed up via
// the onUseAgain callback, mirroring the journey "Prepare in Send" hand-off).
//
// READ / PREVIEW PANE: clicking a row opens a detail drawer on the right that
// fetches GET /api/communications/outbox/{id} (getOutboundMessage) and shows the
// ACTUAL sent message — recipient(s), channel, date+time sent, status, subject,
// and the rendered body (email HTML / SMS text). The table columns shift left to
// make room. Retry / Cancel / Use again stay available both in the row and in
// the open pane.
//
// Channel fidelity: we pass item.channel through verbatim and map it to the
// Create screen's type. The old hub coerced SMS→email here; we do NOT — an SMS
// row reopens as SMS, an email row reopens as email.

const TAB_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'failed', label: 'Needs Attention' },
];

function statusClass(status) {
  const s = String(status || '').toLowerCase().replace(/\s+/g, '_');
  return `ci-stat ci-${s}`;
}

function matchesTab(row, tab) {
  if (tab === 'all') return true;
  const s = String(row.status || '').toLowerCase();
  if (tab === 'scheduled') return ['scheduled', 'queued', 'pending_approval', 'sending'].includes(s);
  if (tab === 'sent') return ['sent'].includes(s);
  if (tab === 'delivered') return ['delivered', 'opened'].includes(s);
  if (tab === 'failed') return ['failed', 'stuck', 'canceled'].includes(s);
  return true;
}

// Map a stored outbox channel to the Create screen's `type`. CRITICAL: preserve
// SMS exactly — only an explicit 'sms' channel becomes 'sms'; everything else
// (email, and the empty default) opens as 'email'. This is the bug the old hub
// had backwards (it coerced SMS into email).
function channelToCreateType(channel) {
  return String(channel || '').toLowerCase() === 'sms' ? 'sms' : 'email';
}

function isSmsChannel(channel) {
  return String(channel || '').toLowerCase() === 'sms';
}

// The "who" for a row. Prefers a real recipient name when the row carries one
// (single-recipient sends, or a "Name +N" summary from the backend); otherwise a
// clear count. With live data this reads as the actual person; the demo seed has
// no per-row names yet, so it shows the count — the name always shows in the
// detail pane when you open a row.
function recipientLabel(r) {
  if (r.recipient_summary) return r.recipient_summary;
  if (r.recipient_name) return r.recipient_name;
  const n = r.recipient_count;
  if (n == null) return '—';
  return n === 1 ? '1 recipient' : `${n} recipients`;
}

// Quiet secondary context under the recipient: the subject (email) or message
// preview (SMS). Never the literal "Communication" placeholder — if there's
// nothing meaningful, we render nothing rather than noise.
function messageSubline(r) {
  const subj = String(r.subject || '').trim();
  if (subj && subj.toLowerCase() !== 'communication') return subj.slice(0, 80);
  const prev = String(r.preview || '').trim();
  if (prev && prev.toLowerCase() !== 'communication') return prev.slice(0, 80);
  return '';
}

// Compact list-row engagement summary for new-pipeline (outbound) rows: how many
// recipients opened / clicked, from the backend aggregate. Returns '' when there
// is no aggregate (legacy single email_logs rows already show opened/clicked via
// their Status column). Honest: only shows counts that actually exist.
function engagementSummary(r) {
  if (r.opened_count == null && r.clicked_count == null) return '';
  const parts = [];
  if (r.opened_count) parts.push(`${r.opened_count} opened`);
  if (r.clicked_count) parts.push(`${r.clicked_count} clicked`);
  return parts.join(' · ');
}

// Pull the truest rendered subject for a sent message: prefer the per-recipient
// snapshot (what that person actually received), then the message-level
// snapshot, then the template snapshot.
function resolveSubject(message, recipients) {
  const rcpSnap = recipients?.[0]?.message_snapshot || {};
  const msgSnap = message?.message_snapshot || {};
  const tpl = message?.template_snapshot || {};
  return rcpSnap.subject || msgSnap.subject || tpl.subject || tpl.name || '(no subject)';
}

// Pull the truest rendered body. Email = HTML, SMS = plain text. We prefer the
// per-recipient snapshot (the exact thing that went out to that recipient),
// falling back to the message-level snapshot.
function resolveBody(message, recipients) {
  const sms = isSmsChannel(message?.channel);
  const rcpSnap = recipients?.[0]?.message_snapshot || {};
  const msgSnap = message?.message_snapshot || {};
  if (sms) {
    const text = rcpSnap.text || msgSnap.text || '';
    const media = (rcpSnap.media_urls && rcpSnap.media_urls.length ? rcpSnap.media_urls : msgSnap.media_urls) || [];
    return { kind: 'sms', text, media };
  }
  const html = rcpSnap.html || msgSnap.html || '';
  return { kind: 'email', html };
}

// ---------------------------------------------------------------------------
// Detail / read pane. Fetches the full sent record and renders exactly what was
// sent, to whom, and when. Self-contained styling (inline) so it doesn't touch
// the shared stylesheet.
// ---------------------------------------------------------------------------
function OutboxDetailPane({ row, onClose, onRetry, onCancel, onUseAgain, busyId }) {
  const [detail, setDetail] = useState(null); // { message, recipients, ... } | null
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(false);
    setDetail(null);
    if (!row?.id) { setLoading(false); setErr(true); return undefined; }
    getOutboundMessage(row.id)
      .then((d) => { if (alive) { setDetail(d || {}); setLoading(false); } })
      .catch(() => { if (alive) { setErr(true); setLoading(false); } });
    return () => { alive = false; };
  }, [row?.id]);

  const message = detail?.message || null;
  const recipients = Array.isArray(detail?.recipients) ? detail.recipients : [];

  // Fall back to the row data we already have if the detail call is still in
  // flight or unavailable, so the header is never empty.
  const channel = (message?.channel || row?.channel || 'email');
  const sms = isSmsChannel(channel);
  const status = message?.status || row?.status;
  const sentAt = message?.sent_at || message?.created_at || row?.sent_at || row?.created_at;
  const subject = message ? resolveSubject(message, recipients) : (row?.subject || row?.template_name || 'Communication');
  const recipientCount = message?.recipient_count != null ? message.recipient_count : row?.recipient_count;
  const body = message ? resolveBody(message, recipients) : null;
  const ChannelIcon = sms ? MessageCircle : Mail;

  const s = String(status || '').toLowerCase();
  const canRetry = ['failed', 'stuck', 'partially_failed'].includes(s);
  const canCancel = ['scheduled', 'queued', 'pending_approval', 'sending'].includes(s);

  const labelStyle = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--ci-txt3)', fontWeight: 700, marginBottom: 4 };
  const valueStyle = { fontSize: 13, color: 'var(--ci-txt)' };

  return (
    <div
      style={{
        width: 'min(430px, 42vw)',
        flex: 'none',
        borderRadius: 16,
        background: 'var(--ci-panel)',
        border: '1px solid var(--ci-bd)',
        backdropFilter: 'blur(14px)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '72vh',
        overflow: 'hidden',
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 12,
      }}
      role="complementary"
      aria-label="Sent message details"
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 16px 12px', borderBottom: '1px solid var(--ci-bd)' }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center', background: 'var(--ci-goldsoft)', color: 'var(--ci-gold-hi)', border: '1px solid var(--ci-bdgold)', flex: 'none' }}>
          <ChannelIcon size={15} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ci-txt)', lineHeight: 1.3, wordBreak: 'break-word' }}>{subject}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 2 }}>Operational record · what was sent</div>
        </div>
        <button
          className="ci-btn ci-ghost ci-sm"
          onClick={onClose}
          aria-label="Close details"
          title="Close"
          style={{ padding: 6, flex: 'none' }}
        >
          <X size={15} />
        </button>
      </div>

      {/* Scroll body */}
      <div style={{ overflowY: 'auto', padding: 16 }}>
        {/* Facts grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div>
            <div style={labelStyle}>Channel</div>
            <div style={{ ...valueStyle, textTransform: 'capitalize' }}>{sms ? 'SMS' : (channel || 'Email')}</div>
          </div>
          <div>
            <div style={labelStyle}>Status</div>
            <div><span className={statusClass(status)}>{titleizeStatus(status)}</span></div>
          </div>
          <div>
            <div style={labelStyle}><Clock size={10} style={{ verticalAlign: -1, marginRight: 4 }} />Sent / Scheduled</div>
            <div style={valueStyle}>{shortDateTime(sentAt)}</div>
          </div>
          <div>
            <div style={labelStyle}><Users size={10} style={{ verticalAlign: -1, marginRight: 4 }} />Audience</div>
            <div style={valueStyle}>{recipientCount != null ? `${recipientCount} recipient${recipientCount === 1 ? '' : 's'}` : '—'}</div>
          </div>
        </div>

        {message?.failure_reason ? (
          <div style={{ fontSize: 12, color: 'var(--ci-rose)', background: 'rgba(217, 138, 160, 0.1)', border: '1px solid rgba(217, 138, 160, 0.3)', borderRadius: 10, padding: '8px 11px', marginBottom: 16 }}>
            <b>Issue:</b> {message.failure_reason}
          </div>
        ) : null}

        {/* Recipients */}
        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>Recipient{recipients.length === 1 ? '' : 's'}</div>
          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--ci-txt3)' }}>Loading recipients…</div>
          ) : recipients.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--ci-txt2)' }}>
              {recipientCount ? `${recipientCount} recipient${recipientCount === 1 ? '' : 's'} (per-recipient detail not recorded for this record).` : 'No recipient detail recorded.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 150, overflowY: 'auto' }}>
              {recipients.map((r) => (
                <div key={r._id || r.email || r.phone} style={{ background: 'var(--ci-panel2)', border: '1px solid var(--ci-bd)', borderRadius: 10, padding: '8px 11px' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ci-txt)' }}>{r.guest_name || r.email || r.phone || 'Recipient'}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ci-txt2)', marginTop: 1 }}>
                    {sms ? (r.phone || '—') : (r.email || '—')}
                    {r.status ? <span style={{ color: 'var(--ci-txt3)' }}>{' · '}{titleizeStatus(r.status)}</span> : null}
                  </div>
                  {r.failure_reason ? <div style={{ fontSize: 11, color: 'var(--ci-rose)', marginTop: 2 }}>{r.failure_reason}</div> : null}
                  {!sms && r.engagement ? (
                    <div style={{ fontSize: 11, marginTop: 3, color: 'var(--ci-txt3)' }}>
                      {r.engagement.tracked ? (
                        <>
                          {(r.engagement.bounced_at || r.engagement.delivery_status === 'bounced') ? (
                            <span style={{ color: 'var(--ci-rose)' }}>Bounced{r.engagement.bounce_reason ? ` — ${r.engagement.bounce_reason}` : ''}</span>
                          ) : (
                            <span style={{ color: r.engagement.opened ? 'var(--ci-green)' : 'var(--ci-txt3)' }}>
                              {r.engagement.opened ? `Opened${r.engagement.open_count > 1 ? ` ${r.engagement.open_count}×` : ''}` : `${titleizeStatus(r.engagement.delivery_status || 'delivered')} · not opened`}
                            </span>
                          )}
                          {r.engagement.clicked ? <span style={{ color: 'var(--ci-gold-hi)' }}>{' · '}Clicked{r.engagement.click_count > 1 ? ` ${r.engagement.click_count}×` : ''}</span> : null}
                          {r.engagement.last_event_at ? <span>{' · last activity '}{shortDateTime(r.engagement.last_event_at)}</span> : null}
                        </>
                      ) : (
                        <span title="No SendGrid delivery-webhook events recorded for this recipient yet">Open/click tracking not recorded yet — requires the email delivery webhook</span>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* The actual message that went out */}
        <div>
          <div style={labelStyle}>Message {sms ? 'text' : 'body'} — as sent</div>
          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--ci-txt3)' }}>Loading message…</div>
          ) : err ? (
            <div style={{ fontSize: 12.5, color: 'var(--ci-txt2)' }}>Could not load the sent message body.</div>
          ) : body?.kind === 'sms' ? (
            body.text ? (
              <>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, color: 'var(--ci-txt)', background: 'var(--ci-panel2)', border: '1px solid var(--ci-bd)', borderRadius: 12, padding: '12px 13px', lineHeight: 1.5 }}>
                  {body.text}
                </div>
                {body.media && body.media.length > 0 ? (
                  <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 6 }}>{body.media.length} media attachment{body.media.length === 1 ? '' : 's'}</div>
                ) : null}
              </>
            ) : (
              <div style={{ fontSize: 12.5, color: 'var(--ci-txt2)' }}>No SMS text recorded for this record.</div>
            )
          ) : body?.html ? (
            <div
              style={{ background: '#ffffff', color: '#1a1a1a', borderRadius: 12, padding: '14px 16px', maxHeight: 360, overflowY: 'auto', fontSize: 13.5, lineHeight: 1.55, border: '1px solid var(--ci-bd)' }}
              // The admin's own previously-sent email content. Rendered so Manny
              // can read exactly what went out, formatted as the recipient saw it.
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(body.html) }}
            />
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--ci-txt2)' }}>
              {row?.preview ? <span style={{ fontStyle: 'italic' }}>{row.preview}</span> : 'No message body recorded for this record.'}
            </div>
          )}
        </div>
      </div>

      {/* Footer actions — mirror the row so Retry/Cancel/Use again stay reachable */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px 16px', borderTop: '1px solid var(--ci-bd)' }}>
        {canRetry && (
          <button className="ci-btn ci-sm" disabled={busyId === row.id} onClick={() => onRetry(row)}><RotateCcw size={12} /> Retry</button>
        )}
        {canCancel && (
          <button className="ci-btn ci-sm" disabled={busyId === row.id} onClick={() => onCancel(row)}><XCircle size={12} /> Cancel</button>
        )}
        <button className="ci-btn ci-ghost ci-sm" style={{ marginLeft: 'auto' }} onClick={() => onUseAgain(row)} title={`Re-open Create pre-filled (${sms ? 'SMS' : 'email'})`}>
          <Copy size={12} /> Use again
        </button>
      </div>
    </div>
  );
}

export default function OutboxScreen({ onUseAgain }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState('all');
  const [busyId, setBusyId] = useState('');
  const [openId, setOpenId] = useState(''); // row whose detail pane is open
  const { show, node: toastNode } = useToast();

  const load = () => {
    setErr(false);
    getCommunicationsOutbox({ limit: 100 })
      .then((d) => {
        // Endpoint returns { rows, ... } or a bare array depending on version.
        const list = Array.isArray(d) ? d : (Array.isArray(d?.rows) ? d.rows : (Array.isArray(d?.items) ? d.items : []));
        setRows(list);
      })
      .catch(() => { setErr(true); setRows([]); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => (rows || []).filter((r) => matchesTab(r, tab)), [rows, tab]);

  // If the open row falls out of the current filter (e.g. tab switch), close the pane.
  useEffect(() => {
    if (openId && !filtered.some((r) => r.id === openId)) setOpenId('');
  }, [filtered, openId]);

  const openRow = filtered.find((r) => r.id === openId) || null;

  const doRetry = async (row) => {
    if (!row.id || busyId) return;
    setBusyId(row.id);
    try {
      await retryOutboundMessage(row.id);
      show('Retry requested — refreshing outbox');
      load();
    } catch {
      show('Could not retry this message');
    } finally {
      setBusyId('');
    }
  };
  const doCancel = async (row) => {
    if (!row.id || busyId) return;
    setBusyId(row.id);
    try {
      await cancelOutboundMessage(row.id, 'Canceled from Communications Intelligence');
      show('Cancellation requested — refreshing outbox');
      load();
    } catch {
      show('Could not cancel this message');
    } finally {
      setBusyId('');
    }
  };

  // Re-open Create Communication pre-filled from this sent row. We hand the
  // parent a normalized prefill payload and let it switch to the Create screen.
  // item.channel is preserved verbatim (mapped to the Create `type`) so SMS
  // reopens as SMS and email as email.
  const doUseAgain = (row) => {
    if (!onUseAgain) return;
    onUseAgain({
      channel: row.channel || 'email',          // raw channel, preserved
      type: channelToCreateType(row.channel),   // Create-screen type ('sms' | 'email')
      templateId: row.template_id ? String(row.template_id) : '',
      subject: row.subject || '',
    });
  };

  const toggleOpen = (row) => {
    if (!row.id) return;
    setOpenId((cur) => (cur === row.id ? '' : row.id));
  };

  if (rows === null) return <div className="ci-view"><Loading label="Loading outbox…" /></div>;

  return (
    <div className="ci-view">
      <div className="ci-ob-tabs">
        {TAB_FILTERS.map((t) => (
          <button key={t.key} className={`ci-ob-tab ${tab === t.key ? 'ci-on' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
        <button className="ci-btn ci-sm" style={{ marginLeft: 'auto' }} onClick={load}><RefreshCw size={13} /> Refresh</button>
      </div>

      {err && (
        <ComingWave title="Outbox is unavailable right now" tag="Live data">
          The outbox feed couldn't be reached. <button className="ci-btn ci-sm" style={{ marginTop: 8 }} onClick={load}>Try again</button>
        </ComingWave>
      )}

      {!err && filtered.length === 0 && (
        <div className="ci-glass" style={{ padding: 26 }}>
          <div className="ci-empty">No communications {tab === 'all' ? 'have been sent yet' : `in “${TAB_FILTERS.find(t => t.key === tab)?.label}”`}.</div>
        </div>
      )}

      {!err && filtered.length > 0 && (
        // Two-pane layout: the table shifts left and a read/preview drawer opens
        // on the right when a row is clicked. Without a selection the table fills
        // the full width as before.
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div className="ci-glass" style={{ padding: '6px 6px 2px', flex: 1, minWidth: 0 }}>
            <div className="ci-table-wrap">
              <table className="ci-table">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Sent / Scheduled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const s = String(r.status || '').toLowerCase();
                    const canRetry = ['failed', 'stuck'].includes(s);
                    const canCancel = ['scheduled', 'queued', 'pending_approval', 'sending'].includes(s);
                    const isOpen = r.id === openId;
                    return (
                      <tr
                        key={r.id || `${r.subject}-${r.created_at}`}
                        onClick={() => toggleOpen(r)}
                        style={{
                          cursor: 'pointer',
                          background: isOpen ? 'rgba(198, 167, 90, 0.1)' : undefined,
                          boxShadow: isOpen ? 'inset 3px 0 0 var(--ci-gold)' : undefined,
                        }}
                        aria-selected={isOpen}
                      >
                        <td>
                          <div className="ci-cm-nm">{recipientLabel(r)}</div>
                          {messageSubline(r) ? <div className="ci-cm-sub">{messageSubline(r)}</div> : null}
                          {engagementSummary(r) ? <div className="ci-cm-sub" style={{ color: 'var(--ci-green)' }}>{engagementSummary(r)}</div> : null}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                            {isSmsChannel(r.channel) ? <MessageCircle size={13} /> : <Mail size={13} />}
                            {isSmsChannel(r.channel) ? 'SMS' : 'Email'}
                          </span>
                        </td>
                        <td><span className={statusClass(r.status)}>{titleizeStatus(r.status)}</span></td>
                        <td>{shortDateTime(r.sent_at || r.created_at)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="ci-rowact">
                            {canRetry && <button className="ci-btn ci-sm" disabled={busyId === r.id} onClick={() => doRetry(r)}><RotateCcw size={12} /> Retry</button>}
                            {canCancel && <button className="ci-btn ci-sm" disabled={busyId === r.id} onClick={() => doCancel(r)}><XCircle size={12} /> Cancel</button>}
                            <button className="ci-btn ci-ghost ci-sm" onClick={() => doUseAgain(r)} title={`Re-open Create pre-filled (${r.channel === 'sms' ? 'SMS' : 'email'})`}><Copy size={12} /> Use again</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {openRow && (
            <OutboxDetailPane
              key={openRow.id}
              row={openRow}
              onClose={() => setOpenId('')}
              onRetry={doRetry}
              onCancel={doCancel}
              onUseAgain={doUseAgain}
              busyId={busyId}
            />
          )}
        </div>
      )}

      {isIntelligenceNotebookEnabled() && (<div style={{ marginTop: 16 }}><MiloNotebookItems kind="message_draft" title="Milo Drafts" /></div>)}

      {toastNode}
    </div>
  );
}
